import { useState } from "react";
import { Search, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { T } from "@/lib/testIds";
import { statusIconFor } from "@/components/BrandIcons";

const STATUS_COLOR = {
  "Application Received": "bg-blue-100 text-blue-800",
  "Under Review": "bg-yellow-100 text-yellow-800",
  "Interview": "bg-indigo-100 text-indigo-800",
  "Additional Information Required": "bg-orange-100 text-orange-800",
  "Approved": "bg-emerald-100 text-emerald-800",
  "Not Selected": "bg-red-100 text-red-800",
};

export default function ApplicationStatus() {
  const [appId, setAppId] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    if (!appId || !lastName) { toast.error("Enter application ID and last name"); return; }
    try {
      setLoading(true);
      const { data } = await api.post("/status/lookup", { application_id: appId, last_name: lastName });
      setResult(data);
    } catch (err) {
      setResult(null);
      toast.error(err.response?.data?.detail || "Not found");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 animate-fade-up">
      <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-semibold mb-3">Application Status</div>
      <h1 className="text-3xl sm:text-4xl font-bold text-[#0A192F]">Check your application status</h1>
      <p className="mt-2 text-slate-600">Enter your Application ID and last name to view your current status.</p>

      <form onSubmit={lookup} className="mt-8 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="text-sm font-semibold text-slate-700">Application ID</label>
          <input data-testid={T.status.appIdInput} value={appId} onChange={(e) => setAppId(e.target.value.toUpperCase())} placeholder="MQS-YYYYMMDD-XXXXXX" className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none font-mono" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700">Last Name</label>
          <input data-testid={T.status.lastNameInput} value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none" />
        </div>
        <div className="md:col-span-3">
          <button type="submit" disabled={loading} data-testid={T.status.lookupBtn} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1D4ED8] hover:bg-blue-800 disabled:opacity-60 text-white font-semibold transition-colors">
            <Search className="w-4 h-4" /> {loading ? "Checking…" : "Check Status"}
          </button>
        </div>
      </form>

      {result && (
        <div data-testid={T.status.result} className="mt-6 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm animate-fade-up">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><ClipboardCheck className="w-5 h-5" /></div>
            <div>
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Applicant</div>
              <div className="font-semibold text-[#0A192F]">{result.first_name} {result.last_name}</div>
            </div>
          </div>
          <div className="mt-5 grid sm:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Application ID</div>
              <div className="mt-1 font-mono font-semibold text-[#0A192F]">{result.application_id}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Current Status</div>
              <span className={`mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${STATUS_COLOR[result.status] || "bg-slate-100 text-slate-700"}`}>
                {statusIconFor(result.status, 16)} {result.status}
              </span>
            </div>
            <div>
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Last Updated</div>
              <div className="mt-1 text-sm text-slate-700">{new Date(result.updated_at).toLocaleString()}</div>
            </div>
          </div>

          {result.status === "Additional Information Required" && result.missing_info && (
            <div className="mt-5 rounded-2xl bg-orange-50 border border-orange-200 p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-orange-700">We need this from you</div>
              <p className="mt-2 text-sm text-orange-900 leading-relaxed">{result.missing_info}</p>
              <p className="mt-2 text-xs text-orange-700">Please reply to the confirmation email or contact our team so we can continue reviewing your application.</p>
            </div>
          )}

          {result.history?.length > 0 && (
            <div className="mt-6">
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2">History</div>
              <ol className="border-l-2 border-slate-100 pl-4 space-y-3">
                {[...result.history].reverse().map((h, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#1D4ED8]" />
                    <div className="text-sm font-semibold text-[#0A192F]">{h.status}</div>
                    <div className="text-xs text-slate-500">{new Date(h.at).toLocaleString()}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
